import { useState } from 'react';
import { Plus, Search, Wallet } from 'lucide-react';
import { P } from '@stockpilot/shared';
import { useFormat } from '@/hooks/useAccess';
import {
  useAccountsQuery, useCreateExpenseMutation, useDeleteExpenseMutation,
  useExpenseActionMutation, useRestoreExpenseMutation,
  useExpenseCategoriesQuery, useExpensesQuery, useSuppliersQuery,
  type Expense, type ExpenseStatus,
} from '@/api/endpoints';
import { apiErrorMessage, apiFieldErrors } from '@/api/baseApi';
import { formatDate, formatMoney } from '@/lib/format';
import {
  Alert, Badge, Button, Card, Checkbox, EmptyState, ErrorState, Input, LoadingState, Modal,
  PageHeader, Pagination, Select, Table, Td, Th, Textarea,
} from '@/components/ui';
import { Can } from '@/components/Can';
import { SummaryCards } from '@/components/SummaryCards';
import { DateRangeFilter, exportRangeOf, rangeFromPreset, type DateRange } from '@/components/DateRangeFilter';
import { ExpenseCategoryPicker } from '@/components/ExpenseCategoryPicker';
import { ExportMenu } from '@/components/ExportMenu';
import { ExpenseEditModal } from './ExpenseEditModal';

const STATUS_TONE: Record<ExpenseStatus, 'neutral' | 'brand' | 'success' | 'warning' | 'danger'> = {
  DRAFT: 'neutral',
  PENDING_APPROVAL: 'warning',
  APPROVED: 'brand',
  REJECTED: 'danger',
  PAID: 'success',
};

/** The intervals the scheduler understands. */
const RECURRENCE = [
  ['MONTHLY', 'Every month'],
  ['WEEKLY', 'Every week'],
  ['QUARTERLY', 'Every three months'],
  ['YEARLY', 'Every year'],
  ['DAILY', 'Every day'],
] as const;

const emptyForm = {
  categoryId: '',
  isRecurring: false,
  recurrenceRule: 'MONTHLY',
  description: '',
  amount: '',
  taxAmount: '',
  supplierId: '',
  accountId: '',
  reference: '',
  notes: '',
};

export function ExpensesPage() {
  const format = useFormat();

  const [page, setPage] = useState(1);
  // Today by default: a till screen is about today's takings, and a list
  // that opens on every sale ever made buries them.
  const [range, setRange] = useState<DateRange>(rangeFromPreset('today'));
  const exportRange = exportRangeOf(range);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [failure, setFailure] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [rejecting, setRejecting] = useState<Expense | null>(null);
  const [reason, setReason] = useState('');
  // Paying takes the money out of a real account, so the account is named here
  // rather than assumed — an expense paid from nowhere cannot be balanced.
  const [paying, setPaying] = useState<Expense | null>(null);
  const [payAccountId, setPayAccountId] = useState('');
  const [editing, setEditing] = useState<Expense | null>(null);
  const [deleting, setDeleting] = useState<Expense | null>(null);
  const [deleteReason, setDeleteReason] = useState('');
  const [showHidden, setShowHidden] = useState(false);

  const { data, isLoading, isError, error, refetch, isFetching } = useExpensesQuery({
    from: range.from || undefined,
    to: range.to || undefined,
    page,
    limit: 25,
    search: search || undefined,
    status: status || undefined,
    includeHidden: showHidden || undefined,
  });
  const { data: categories } = useExpenseCategoriesQuery();
  const { data: suppliers } = useSuppliersQuery({ limit: 200, isActive: true });
  const { data: accounts } = useAccountsQuery({ limit: 50 });

  const [createExpense, createState] = useCreateExpenseMutation();
  const [runAction, actionState] = useExpenseActionMutation();
  const [deleteExpense, deleteState] = useDeleteExpenseMutation();
  const [restoreExpense, restoreState] = useRestoreExpenseMutation();

  const fieldErrors = apiFieldErrors(createState.error);

  const confirmDelete = async (): Promise<void> => {
    if (!deleting) return;
    setFailure(null);
    try {
      const result = await deleteExpense({ id: deleting.id, reason: deleteReason }).unwrap();
      setDeleting(null);
      setDeleteReason('');
      setNotice(
        result.cashEntriesReversed > 0
          ? `Deleted. ${formatMoney(deleting.total, format)} has gone back into the account.`
          : 'Deleted. It had not been paid, so no money moved.',
      );
    } catch (error) {
      setFailure(apiErrorMessage(error, 'That expense could not be deleted'));
    }
  };

  const putBack = async (expense: Expense): Promise<void> => {
    setFailure(null);
    try {
      await restoreExpense(expense.id).unwrap();
      setNotice(`${expense.number} is back.`);
    } catch (error) {
      setFailure(apiErrorMessage(error, 'That expense could not be restored'));
    }
  };

  const submit = async (): Promise<void> => {
    setFailure(null);
    try {
      await createExpense({
        categoryId: form.categoryId,
        description: form.description,
        amount: Number(form.amount),
        taxAmount: form.taxAmount ? Number(form.taxAmount) : undefined,
        supplierId: form.supplierId || undefined,
        accountId: form.accountId || undefined,
        reference: form.reference || undefined,
        notes: form.notes || undefined,
        ...(form.isRecurring
          ? { isRecurring: true, recurrenceRule: form.recurrenceRule }
          : {}),
      }).unwrap();
      setCreating(false);
      setForm(emptyForm);
    } catch (createError) {
      setFailure(apiErrorMessage(createError, 'That expense could not be recorded'));
    }
  };

  const act = async (id: string, action: string, body?: Record<string, unknown>): Promise<void> => {
    setFailure(null);
    try {
      await runAction({ id, action, body }).unwrap();
      setRejecting(null);
      setReason('');
    } catch (actionError) {
      setFailure(apiErrorMessage(actionError, `That expense could not be ${action}ed`));
    }
  };

  return (
    <>
      <PageHeader
        title="Expenses"
        description="Money spent that is not stock — rent, fuel, wages. Each one is approved before it is paid."
        action={
          <div className="flex gap-2">
            <ExportMenu
              period={exportRange.label}
              items={[
                {
                  label: 'Expenses — every expense, one per line',
                  path: '/reports/expenses/export',
                  params: { from: exportRange.from, to: exportRange.to },
                },
              ]}
            />
            <Can permission={P.EXPENSE_CREATE}>
              <Button
                icon={Plus}
                onClick={() => setCreating(true)}
                disabled={(categories?.length ?? 0) === 0}
              >
                New expense
              </Button>
            </Can>
          </div>
        }
      />

      {notice && (
        <div className="mb-4">
          <Alert tone="success">{notice}</Alert>
        </div>
      )}

      {failure && (
        <div className="mb-4">
          <Alert tone="danger">{failure}</Alert>
        </div>
      )}

      {data?.summary && (
        <SummaryCards
          stats={[
            { label: 'Expenses', value: String(data.summary.count), hint: 'in this period' },
            { label: 'Total', value: formatMoney(String(data.summary.total), format) },
            {
              label: 'Paid',
              value: formatMoney(String(data.summary.paid), format),
              hint: 'money already out',
            },
            {
              label: 'To pay',
              value: formatMoney(String(data.summary.awaitingPayment), format),
              hint: 'approved, not yet settled',
              tone: 'owed',
            },
          ]}
        />
      )}

      <Card>
        <div className="flex flex-wrap items-end gap-3 border-b border-subtle p-4">
          <DateRangeFilter
            value={range}
            onChange={(next) => {
              setRange(next);
              setPage(1);
            }}
          />
          <div className="min-w-48 flex-1">
            <Input
              label="Search"
              icon={Search}
              placeholder="Number, description or reference"
              value={search}
              onChange={(event) => {
                setSearch(event.target.value);
                setPage(1);
              }}
            />
          </div>
          <div className="w-full sm:w-52">
            <Select
              label="Status"
              value={status}
              onChange={(event) => {
                setStatus(event.target.value);
                setPage(1);
              }}
            >
              <option value="">All statuses</option>
              <option value="DRAFT">Draft</option>
              <option value="PENDING_APPROVAL">Awaiting approval</option>
              <option value="APPROVED">Approved</option>
              <option value="REJECTED">Rejected</option>
              <option value="PAID">Paid</option>
            </Select>
          </div>
          <Can permission={P.EXPENSE_APPROVE}>
            <label className="flex items-center gap-2 pb-2.5 text-sm">
              <input
                type="checkbox"
                className="size-4 rounded border-subtle text-brand-600"
                checked={showHidden}
                onChange={(event) => {
                  setShowHidden(event.target.checked);
                  setPage(1);
                }}
              />
              {/* A deletion nobody can look at is not an audit trail. */}
              Show deleted
            </label>
          </Can>
        </div>

        {isLoading ? (
          <LoadingState />
        ) : isError ? (
          <ErrorState message={apiErrorMessage(error)} onRetry={() => void refetch()} />
        ) : data && data.items.length === 0 ? (
          <EmptyState
            icon={Wallet}
            title={search || status ? 'Nothing matches' : 'No expenses yet'}
            description="Record what the business spends outside of stock."
          />
        ) : (
          <>
            <Table>
              <thead>
                <tr>
                  <Th>Number</Th>
                  <Th>Date</Th>
                  <Th>Description</Th>
                  <Th>Category</Th>
                  <Th numeric>Total</Th>
                  <Th>Status</Th>
                  <Th />
                </tr>
              </thead>
              <tbody className={isFetching ? 'opacity-60' : undefined}>
                {data?.items.map((expense) => (
                  <tr
                    key={expense.id}
                    className={expense.deletedAt ? 'opacity-60 hover:bg-sunken' : 'hover:bg-sunken'}
                  >
                    <Td className="font-medium">
                      {expense.number}
                      {expense.deletedAt && (
                        <p className="text-xs font-normal text-muted">
                          deleted{expense.deleteReason ? ` · ${expense.deleteReason}` : ''}
                        </p>
                      )}
                    </Td>
                    <Td className="text-secondary">{formatDate(expense.expenseDate, format)}</Td>
                    <Td>
                      <p>{expense.description}</p>
                      {expense.rejectReason && (
                        <p className="text-xs text-red-600">{expense.rejectReason}</p>
                      )}
                    </Td>
                    <Td className="text-secondary">{expense.category.name}</Td>
                    <Td numeric>{formatMoney(expense.total, format)}</Td>
                    <Td>
                      <Badge tone={STATUS_TONE[expense.status]}>
                        {expense.status.toLowerCase().replace(/_/g, ' ')}
                      </Badge>
                    </Td>
                    <Td className="text-right">
                      {expense.deletedAt ? (
                        <Can permission={P.EXPENSE_APPROVE}>
                          <Button
                            size="sm"
                            variant="ghost"
                            loading={restoreState.isLoading}
                            onClick={() => void putBack(expense)}
                          >
                            Restore
                          </Button>
                        </Can>
                      ) : (
                        <>
                          <Can permission={P.EXPENSE_CREATE}>
                            <Button size="sm" variant="ghost" onClick={() => setEditing(expense)}>
                              Edit
                            </Button>
                          </Can>
                          <Can permission={P.EXPENSE_APPROVE}>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => {
                                setFailure(null);
                                setDeleting(expense);
                                setDeleteReason('');
                              }}
                            >
                              Delete
                            </Button>
                          </Can>
                        </>
                      )}
                      {expense.status === 'DRAFT' && (
                        <Can permission={P.EXPENSE_CREATE}>
                          <Button
                            size="sm"
                            variant="ghost"
                            loading={actionState.isLoading}
                            onClick={() => void act(expense.id, 'submit')}
                          >
                            Submit
                          </Button>
                        </Can>
                      )}
                      {expense.status === 'PENDING_APPROVAL' && (
                        <Can permission={P.EXPENSE_APPROVE}>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => {
                              setFailure(null);
                              setRejecting(expense);
                            }}
                          >
                            Reject
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            loading={actionState.isLoading}
                            onClick={() => void act(expense.id, 'approve')}
                          >
                            Approve
                          </Button>
                        </Can>
                      )}
                      {expense.status === 'APPROVED' && (
                        <Can permission={P.EXPENSE_APPROVE}>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => {
                              setPaying(expense);
                              setPayAccountId(expense.accountId ?? '');
                            }}
                          >
                            Mark paid
                          </Button>
                        </Can>
                      )}
                    </Td>
                  </tr>
                ))}
              </tbody>
            </Table>

            {data && (
              <Pagination
                page={data.meta.page}
                totalPages={data.meta.totalPages}
                total={data.meta.total}
                onPageChange={setPage}
              />
            )}
          </>
        )}
      </Card>

      <Modal
        open={creating}
        onClose={() => setCreating(false)}
        title="New expense"
        description="Recorded as a draft. It has to be approved before it can be paid."
        footer={
          <>
            <Button variant="outline" onClick={() => setCreating(false)}>Cancel</Button>
            <Button
              onClick={() => void submit()}
              loading={createState.isLoading}
              disabled={!form.categoryId || !form.description || !form.amount}
            >
              Record expense
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          {createState.error && <Alert tone="danger">{apiErrorMessage(createState.error)}</Alert>}

          <ExpenseCategoryPicker
            required
            value={form.categoryId}
            onChange={(categoryId) => setForm({ ...form, categoryId })}
            categories={categories ?? []}
            error={fieldErrors.categoryId}
          />

          <Input
            label="Description"
            required
            placeholder="Generator fuel for June"
            value={form.description}
            onChange={(event) => setForm({ ...form, description: event.target.value })}
            error={fieldErrors.description}
          />

          <div className="grid gap-4 sm:grid-cols-2">
            <Input
              label="Amount"
              required
              type="number"
              min="0"
              step="0.01"
              value={form.amount}
              onChange={(event) => setForm({ ...form, amount: event.target.value })}
              error={fieldErrors.amount}
            />
            <Input
              label="Tax"
              type="number"
              min="0"
              step="0.01"
              value={form.taxAmount}
              onChange={(event) => setForm({ ...form, taxAmount: event.target.value })}
            />
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <Select
              label="Paid to"
              value={form.supplierId}
              onChange={(event) => setForm({ ...form, supplierId: event.target.value })}
              hint="Only if they are a supplier on file."
            >
              <option value="">Not a supplier</option>
              {suppliers?.items.map((supplier) => (
                <option key={supplier.id} value={supplier.id}>{supplier.name}</option>
              ))}
            </Select>
            <Input
              label="Reference"
              value={form.reference}
              onChange={(event) => setForm({ ...form, reference: event.target.value })}
            />
          </div>

          <Select
            label="Paid from"
            value={form.accountId}
            onChange={(event) => setForm({ ...form, accountId: event.target.value })}
            hint="Optional now — it is asked for again when the expense is paid."
          >
            <option value="">Decide later</option>
            {accounts?.items
              .filter((account) => account.isActive)
              .map((account) => (
                <option key={account.id} value={account.id}>{account.name}</option>
              ))}
          </Select>

          <Textarea
            label="Notes"
            rows={2}
            value={form.notes}
            onChange={(event) => setForm({ ...form, notes: event.target.value })}
          />

          <div className="rounded-lg border border-subtle p-3">
            <Checkbox
              label="This expense comes round again"
              description="Rent, wages, licences. Each repeat is raised as a draft for someone to check — nothing is paid automatically."
              checked={form.isRecurring}
              onChange={(event) => setForm({ ...form, isRecurring: event.target.checked })}
            />
            {form.isRecurring && (
              <div className="mt-3">
                <Select
                  label="How often"
                  value={form.recurrenceRule}
                  onChange={(event) => setForm({ ...form, recurrenceRule: event.target.value })}
                >
                  {RECURRENCE.map(([value, label]) => (
                    <option key={value} value={value}>{label}</option>
                  ))}
                </Select>
              </div>
            )}
          </div>
        </div>
      </Modal>

      {editing && (
        <ExpenseEditModal
          expense={editing}
          onClose={() => setEditing(null)}
          onSaved={() => setNotice('The expense has been updated.')}
        />
      )}

      {deleting && (
        <Modal
          open
          onClose={() => setDeleting(null)}
          size="sm"
          title={`Delete ${deleting.number}?`}
          footer={
            <>
              <Button variant="outline" onClick={() => setDeleting(null)}>Cancel</Button>
              <Button
                variant="danger"
                loading={deleteState.isLoading}
                onClick={() => void confirmDelete()}
              >
                Delete it
              </Button>
            </>
          }
        >
          <div className="space-y-3">
            <p className="text-sm text-secondary">
              {deleting.status === 'PAID' ? (
                <>
                  It comes off the list and{' '}
                  <span className="font-medium">
                    {formatMoney(deleting.total, format)} goes back into
                    {deleting.account ? ` ${deleting.account.name}` : ' the account that paid it'}
                  </span>{' '}
                  as a dated reversal.
                </>
              ) : (
                'It comes off the list. No money had been paid, so nothing moves.'
              )}{' '}
              Nothing is destroyed — anyone who may approve an expense can still see it and put it
              back.
            </p>
            <Input
              label="Reason"
              placeholder="Entered twice"
              value={deleteReason}
              onChange={(event) => setDeleteReason(event.target.value)}
            />
          </div>
        </Modal>
      )}

      <Modal
        open={rejecting !== null}
        onClose={() => setRejecting(null)}
        title="Reject this expense?"
        description="It goes back to the person who raised it. Editing it puts it in the queue again."
        footer={
          <>
            <Button variant="outline" onClick={() => setRejecting(null)}>Cancel</Button>
            <Button
              variant="danger"
              loading={actionState.isLoading}
              disabled={!reason}
              onClick={() => rejecting && void act(rejecting.id, 'reject', { reason })}
            >
              Reject
            </Button>
          </>
        }
      >
        <Input label="Reason" value={reason} onChange={(event) => setReason(event.target.value)} />
      </Modal>

      <Modal
        open={paying !== null}
        onClose={() => setPaying(null)}
        title="Which account paid this?"
        description="The money comes out of that account, so the day still balances."
        size="sm"
        footer={
          <>
            <Button variant="outline" onClick={() => setPaying(null)}>Cancel</Button>
            <Button
              loading={actionState.isLoading}
              disabled={!payAccountId}
              onClick={() => {
                if (!paying) return;
                void act(paying.id, 'pay', { accountId: payAccountId }).then(() =>
                  setPaying(null),
                );
              }}
            >
              Pay it
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          {paying && (
            <Alert tone="info">
              {paying.description} — {formatMoney(paying.amount, format)}
            </Alert>
          )}
          <Select
            label="Paid from"
            required
            value={payAccountId}
            onChange={(event) => setPayAccountId(event.target.value)}
          >
            <option value="">Select an account</option>
            {accounts?.items
              .filter((account) => account.isActive)
              .map((account) => (
                <option key={account.id} value={account.id}>
                  {account.name} — {formatMoney(account.balance, format)}
                </option>
              ))}
          </Select>
        </div>
      </Modal>
    </>
  );
}
